import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, LazyMotion, domAnimation } from 'framer-motion';

interface Notification {
  id: string;
  message: string;
  timestamp: Date;
}

interface NotificationCounterProps {
  notifications: Notification[];
  onDismiss: (id: string) => void;
  currentUserId?: string;
}

const NotificationCounter: React.FC<NotificationCounterProps> = ({ notifications, onDismiss }) => {
  const [isHovering, setIsHovering] = useState(false);
  const [toastNotifications, setToastNotifications] = useState<Notification[]>([]);

  // Handle new notifications
  useEffect(() => {
    // Add new notifications to toast list
    const newNotifications = notifications.filter(
      n => !toastNotifications.some(tn => tn.id === n.id)
    );
    if (newNotifications.length > 0) {
      setToastNotifications(prev => [...prev, ...newNotifications]);
    }
  }, [notifications, toastNotifications]);

  // Auto-dismiss toast notifications after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setToastNotifications(prev => prev.slice(1));
    }, 5000);

    return () => clearTimeout(timer);
  }, [toastNotifications]);

  const handleToastDismiss = (id: string) => {
    setToastNotifications(prev => prev.filter(n => n.id !== id));
  };

  const handleClearAll = () => {
    // Clear all notifications from the panel
    notifications.forEach(n => onDismiss(n.id));
    // Also clear any remaining toast notifications
    setToastNotifications([]);
  };

  return (
    <LazyMotion features={domAnimation}>
      <div className="relative">
        {/* Counter Badge */}
        <div 
          className="fixed top-4 right-4 z-50"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <div className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center cursor-pointer shadow-lg">
            {notifications.length}
          </div>

          {/* Notifications Panel */}
          <AnimatePresence>
            {isHovering && notifications.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl overflow-hidden"
              >
                <div className="p-2 bg-purple-600 text-white flex justify-between items-center">
                  <span className="font-semibold">Recent Updates</span>
                  <button 
                    onClick={handleClearAll}
                    className="text-sm hover:text-purple-200"
                  >
                    Clear All
                  </button>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifications.map(notification => (
                    <motion.div
                      key={notification.id}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="p-3 border-b border-gray-100 hover:bg-gray-50"
                    >
                      <div className="flex justify-between items-start">
                        <p className="text-sm text-gray-700">{notification.message}</p>
                        <button
                          onClick={() => onDismiss(notification.id)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          ×
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {notification.timestamp.toLocaleTimeString()}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Toast Notifications - Auto-dismissing */}
        <div className="fixed bottom-4 right-4 z-50 space-y-2">
          <AnimatePresence mode="popLayout">
            {toastNotifications.map(notification => (
              <motion.div
                key={notification.id}
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-lg shadow-lg p-3 max-w-sm"
              >
                <div className="flex justify-between items-start">
                  <p className="text-sm text-gray-700">{notification.message}</p>
                  <button
                    onClick={() => handleToastDismiss(notification.id)}
                    className="text-gray-400 hover:text-gray-600 ml-2"
                  >
                    ×
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {notification.timestamp.toLocaleTimeString()}
                </p>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </LazyMotion>
  );
};

export default NotificationCounter; 